import React, { createContext, useState, useContext, useEffect } from 'react';
import { puede } from '../utils/permisos';
import {
  login as loginApi, getMe, clienteLogin as clienteLoginApi, getClienteMe, refreshToken,
  abrirSesionVencida, cerrarSesionVencida, sesionVencida,
} from '../services/api';
import { guardarCredencialOffline, verificarCredencialOffline, actualizarTokenOffline } from '../utils/authOffline';
import { precargarDatosOffline } from '../utils/precargaOffline';

const expDelToken = (t) => {
  try { return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp || 0; } catch { return 0; }
};
// Renovación deslizante: con actividad, el token se renueva si le queda menos de esto (vida total 12 h)
const RENOVAR_SI_QUEDAN_MS = 11 * 3600 * 1000;
const REVISAR_CADA_MS = 5 * 60 * 1000;

const esErrorDeRed = (err) => !err?.response || err?.code === 'ERR_NETWORK' || err?.message === 'Network Error';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [userType, setUserType] = useState(null); // 'user' o 'cliente'
  const [loading, setLoading] = useState(true);
  const [sesionOffline, setSesionOffline] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [avisoSesionVencida, setAvisoSesionVencida] = useState(false);

  useEffect(() => {
    const abrir = () => setAvisoSesionVencida(true);
    // Otra pestaña ya volvió a ingresar: liberar los pedidos en espera también aquí
    const onStorage = (e) => {
      if (e.key === 'token' && e.newValue && sesionVencida()) { cerrarSesionVencida(true); setAvisoSesionVencida(false); setToken(e.newValue); }
    };
    window.addEventListener('freshen:sesion-vencida', abrir);
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('freshen:sesion-vencida', abrir); window.removeEventListener('storage', onStorage); };
  }, []);

  // Mientras el usuario trabaja, renovar el token antes de que venza (una sesión activa nunca caduca).
  useEffect(() => {
    if (userType !== 'user' || sesionOffline) return undefined;
    let ultimo = 0;
    const revisar = () => {
      if (Date.now() - ultimo < REVISAR_CADA_MS || !navigator.onLine || sesionVencida()) return;
      ultimo = Date.now();
      const actual = localStorage.getItem('token');
      if (!actual || expDelToken(actual) * 1000 - Date.now() > RENOVAR_SI_QUEDAN_MS) return;
      refreshToken().then((r) => {
        localStorage.setItem('token', r.data.access_token);
        setToken(r.data.access_token);
        actualizarTokenOffline(r.data.access_token);
      }).catch(() => {}); // si ya venció, el interceptor abre el aviso de sesión caducada
    };
    const alVolver = () => { if (document.visibilityState === 'visible') { ultimo = 0; revisar(); } };
    window.addEventListener('pointerdown', revisar, { passive: true });
    window.addEventListener('keydown', revisar, { passive: true });
    document.addEventListener('visibilitychange', alVolver);
    revisar();
    return () => {
      window.removeEventListener('pointerdown', revisar);
      window.removeEventListener('keydown', revisar);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [userType, sesionOffline]);

  useEffect(() => {
    // Si el servidor niega una sección (el admin cambió las pestañas), se refrescan los permisos del usuario
    let ultima = 0;
    const refrescar = async () => {
      if (Date.now() - ultima < 5000 || localStorage.getItem('userType') !== 'user') return;
      ultima = Date.now();
      try {
        const r = await getMe();
        setUser(r.data);
        localStorage.setItem('user', JSON.stringify(r.data));
      } catch (e) { /* sin red: se reintenta en el próximo aviso */ }
    };
    window.addEventListener('freshen:permisos', refrescar);
    return () => window.removeEventListener('freshen:permisos', refrescar);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      const savedUserType = localStorage.getItem('userType');
      
      if (savedToken && savedUserType) {
        try {
          if (savedUserType === 'cliente') {
            const response = await getClienteMe();
            setCliente(response.data);
            setUserType('cliente');
          } else {
            const response = await getMe();
            setUser(response.data);
            setUserType('user');
            localStorage.setItem('user', JSON.stringify(response.data));
            precargarDatosOffline(response.data);
          }
          setToken(savedToken);
        } catch (error) {
          // Sin red (o el SW devolvió 503 offline): mantener la sesión guardada en el dispositivo.
          const status = error?.response?.status;
          const sinRed = esErrorDeRed(error) || status === 503 || status >= 500;
          const guardado = savedUserType === 'cliente' ? localStorage.getItem('cliente') : localStorage.getItem('user');
          if (sinRed && guardado) {
            console.warn('Sin conexión: sesión restaurada desde el dispositivo');
            if (savedUserType === 'cliente') setCliente(JSON.parse(guardado)); else setUser(JSON.parse(guardado));
            setUserType(savedUserType);
            setToken(savedToken);
            setSesionOffline(true);
            localStorage.setItem('freshen_sesion_offline', '1');
            // El servidor puede estar arrancando: re-verificar de fondo hasta que responda y sincronizar lo pendiente
            if (savedUserType !== 'cliente') {
              let intentos = 0;
              const reverificar = async () => {
                if (intentos++ >= 40) return;
                if (!navigator.onLine) { setTimeout(reverificar, 15000); return; }
                try {
                  const r = await getMe();
                  setUser(r.data);
                  localStorage.setItem('user', JSON.stringify(r.data));
                  localStorage.removeItem('freshen_sesion_offline');
                  setSesionOffline(false);
                  precargarDatosOffline(r.data);
                  window.dispatchEvent(new Event('freshen:sincronizar'));
                } catch (e) {
                  if (e?.response?.status === 401) {
                    // Token vencido con el servidor ya activo: pedir credenciales de nuevo (lo pendiente queda guardado)
                    localStorage.removeItem('freshen_sesion_offline');
                    setSesionOffline(false);
                    abrirSesionVencida();
                  } else setTimeout(reverificar, 15000);
                }
              };
              setTimeout(reverificar, 5000);
            }
          } else {
            console.error('Error verifying token:', error);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('cliente');
            localStorage.removeItem('userType');
            setToken(null);
            setUser(null);
            setCliente(null);
            setUserType(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const aplicarSesion = (accessToken, userData, offline) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userType', 'user');
    if (offline) localStorage.setItem('freshen_sesion_offline', '1'); else localStorage.removeItem('freshen_sesion_offline');
    setToken(accessToken);
    setUser(userData);
    setUserType('user');
    setSesionOffline(offline);
  };

  const servidorCaido = (error) => esErrorDeRed(error) || error?.response?.status >= 500 || error?.code === 'ECONNABORTED';
  const esperar = (ms) => new Promise((r) => setTimeout(r, ms));
  // Reintentos mientras el servidor "despierta" (arranque en frío): ~40 s en total
  const ESPERAS_MS = [2000, 3000, 5000, 8000, 10000, 12000];

  // Entró con la credencial guardada en el dispositivo mientras el servidor no respondía:
  // seguir intentando de fondo para obtener un token fresco y sincronizar lo pendiente.
  const refrescarSesionEnSegundoPlano = (email, password) => {
    let intentos = 0;
    const tick = async () => {
      if (intentos++ >= 40) return;
      if (!navigator.onLine) { setTimeout(tick, 15000); return; }
      try {
        const r = await loginApi(email, password);
        aplicarSesion(r.data.access_token, r.data.user, false);
        guardarCredencialOffline(email, password, r.data.access_token, r.data.user);
        precargarDatosOffline(r.data.user);
        window.dispatchEvent(new Event('freshen:sincronizar'));
      } catch (e) {
        if (servidorCaido(e)) setTimeout(tick, 15000);
        else { localStorage.removeItem('freshen_sesion_offline'); setSesionOffline(false); abrirSesionVencida(); } // credencial ya no válida
      }
    };
    setTimeout(tick, 5000);
  };

  const login = async (email, password, onProgreso) => {
    for (let intento = 0; ; intento++) {
      try {
        const response = await loginApi(email, password);
        const { access_token, user: userData } = response.data;
        aplicarSesion(access_token, userData, false);
        guardarCredencialOffline(email, password, access_token, userData);
        precargarDatosOffline(userData);
        return { success: true };
      } catch (error) {
        if (!servidorCaido(error)) {
          console.error('Login error:', error);
          return { success: false, error: error.response?.data?.detail || 'Error al iniciar sesión' };
        }
        // Servidor sin respuesta (sin red o arrancando): el último usuario del dispositivo entra igual,
        // con su contraseña verificada contra el hash local, y la sesión se refresca sola de fondo.
        const cred = await verificarCredencialOffline(email, password);
        if (cred) {
          aplicarSesion(cred.token, cred.user, true);
          if (navigator.onLine) refrescarSesionEnSegundoPlano(email, password);
          return { success: true, offline: true };
        }
        if (!navigator.onLine || intento >= ESPERAS_MS.length) {
          return {
            success: false,
            error: navigator.onLine
              ? 'El servidor no respondió tras varios intentos. Esperá unos segundos y volvé a intentar.'
              : 'Sin conexión. Solo puede ingresar offline el último usuario que inició sesión en este dispositivo (con su misma contraseña).',
          };
        }
        onProgreso?.({ intento: intento + 1, total: ESPERAS_MS.length });
        await esperar(ESPERAS_MS[intento]);
      }
    }
  };

  const loginCliente = async (email, password) => {
    try {
      const response = await clienteLoginApi(email, password);
      const { access_token, cliente: clienteData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('cliente', JSON.stringify(clienteData));
      localStorage.setItem('userType', 'cliente');
      
      setToken(access_token);
      setCliente(clienteData);
      setUserType('cliente');
      
      return { success: true };
    } catch (error) {
      console.error('Cliente login error:', error);
      return { success: false, error: error.response?.data?.detail || 'Error al iniciar sesión' };
    }
  };

  // Re-ingreso desde el aviso de sesión caducada: la pantalla actual no se desmonta y los pedidos en espera se reintentan.
  const emailSesion = () => {
    try {
      const guardado = JSON.parse(localStorage.getItem(userType === 'cliente' || localStorage.getItem('userType') === 'cliente' ? 'cliente' : 'user'));
      return (userType === 'cliente' ? cliente?.email : user?.email) || guardado?.email || '';
    } catch { return ''; }
  };

  const reautenticar = async (password) => {
    const email = emailSesion();
    const esCliente = localStorage.getItem('userType') === 'cliente';
    try {
      if (esCliente) {
        const r = await clienteLoginApi(email, password);
        localStorage.setItem('token', r.data.access_token);
        localStorage.setItem('cliente', JSON.stringify(r.data.cliente));
        setToken(r.data.access_token);
        setCliente(r.data.cliente);
      } else {
        const r = await loginApi(email, password);
        aplicarSesion(r.data.access_token, r.data.user, false);
        guardarCredencialOffline(email, password, r.data.access_token, r.data.user);
      }
      cerrarSesionVencida(true);
      setAvisoSesionVencida(false);
      return { success: true };
    } catch (error) {
      if (servidorCaido(error)) return { success: false, error: 'El servidor no responde. Esperá unos segundos y volvé a intentar.' };
      return { success: false, error: error.response?.data?.detail || 'No se pudo validar la contraseña' };
    }
  };

  const salirSesionVencida = () => {
    cerrarSesionVencida(false);
    setAvisoSesionVencida(false);
    setLoading(false); // la verificación inicial que esperaba la sesión queda descartada
    logout();
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('cliente');
    localStorage.removeItem('userType');
    localStorage.removeItem('freshen_sesion_offline');
    setToken(null);
    setUser(null);
    setCliente(null);
    setUserType(null);
  };

  const isAdmin = () => user?.rol === 'admin';
  const isTecnico = () => user?.rol === 'tecnico';
  const tienePermiso = (modulo) => puede(user, modulo);
  const isCliente = () => userType === 'cliente';

  return (
    <AuthContext.Provider value={{ 
      user, 
      cliente, 
      userType, 
      token, 
      loading, 
      sesionOffline,
      avisoSesionVencida,
      emailSesion,
      reautenticar,
      salirSesionVencida,
      login, 
      loginCliente,
      logout, 
      isAdmin, 
      isTecnico,
      tienePermiso,
      isCliente
    }}>
      {children}
    </AuthContext.Provider>
  );
};